import { objectWithoutProperties as _objectWithoutProperties, extends as _extends, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, asyncToGenerator as _asyncToGenerator } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../../node_modules/prop-types/index.js';
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import classnames from '../../../node_modules/classnames/index.js';
import { useThemeProps, useFormControl } from '@mui/material';
import Quill from '../../../cinnamon-mui/node_modules/quill/core.js';
export { default as Quill } from '../../../cinnamon-mui/node_modules/quill/core.js';
import { registerQuill, styledQuillEditor } from './quill.js';
import QuillDefaultToolbar from './QuillDefaultToolbar.js';
import QuillInsertLink from './QuillInsertLink.js';
import { createComponentSlot } from '../../../modules/styling/theme.js';

var _excluded = ["value", "onChange", "onChangeSelection", "toolbarComponent", "toolbarProps", "onImageUpload", "allowedImageTypes", "disableImageTransfer", "disableMultipleImages", "sanitizeEmptyHtml", "pasteAsPlainText", "placeholder", "rows", "disabled", "readOnly", "error", "quillRef", "className", "sx", "modules", "registry"],
    _excluded2 = ["disabled", "error"];

registerQuill(); // re-export quill for external configuration
var componentRoot = createComponentSlot({
  name: "CinQuillEditor",
  slot: "Root"
});
var nextToolbarId = 1;
var EMPTY_HTML = "<div></div>";
var DEFAULT_ALLOWED_IMAGE_TYPES = ["image/png", "image/gif", "image/jpeg", "image/bmp"];

var QuillEditorBase = function QuillEditorBase(_ref) {
  var value = _ref.value,
      onChange = _ref.onChange,
      onChangeSelection = _ref.onChangeSelection,
      _ref$toolbarComponent = _ref.toolbarComponent,
      ToolbarComponent = _ref$toolbarComponent === void 0 ? QuillDefaultToolbar : _ref$toolbarComponent,
      toolbarProps = _ref.toolbarProps,
      onImageUpload = _ref.onImageUpload,
      _ref$allowedImageType = _ref.allowedImageTypes,
      allowedImageTypes = _ref$allowedImageType === void 0 ? DEFAULT_ALLOWED_IMAGE_TYPES : _ref$allowedImageType,
      _ref$disableImageTran = _ref.disableImageTransfer,
      disableImageTransfer = _ref$disableImageTran === void 0 ? false : _ref$disableImageTran,
      _ref$disableMultipleI = _ref.disableMultipleImages,
      disableMultipleImages = _ref$disableMultipleI === void 0 ? false : _ref$disableMultipleI,
      _ref$sanitizeEmptyHtm = _ref.sanitizeEmptyHtml,
      sanitizeEmptyHtml = _ref$sanitizeEmptyHtm === void 0 ? true : _ref$sanitizeEmptyHtm,
      _ref$pasteAsPlainText = _ref.pasteAsPlainText,
      pasteAsPlainText = _ref$pasteAsPlainText === void 0 ? false : _ref$pasteAsPlainText,
      placeholder = _ref.placeholder;
      _ref.rows;
      var _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$readOnly = _ref.readOnly,
      readOnly = _ref$readOnly === void 0 ? false : _ref$readOnly;
      _ref.error;
      var quillRef = _ref.quillRef,
      className = _ref.className;
      _ref.sx;
      var modules = _ref.modules,
      registry = _ref.registry;
      _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(modules),
      _useState2 = _slicedToArray(_useState, 1),
      initialModules = _useState2[0]; // modules are only used on initial mount


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      insertLinkOpen = _useState4[0],
      setInsertLinkOpen = _useState4[1];

  var container = useRef(null);
  var quill = useRef(null);
  var editorValue = useRef(value !== null && value !== void 0 ? value : "");
  var editorSelection = useRef(null); // each instance requires a unique toolbar id

  var toolbarId = useRef("");

  if (!toolbarId.current) {
    toolbarId.current = "quill-toolbar-".concat(nextToolbarId);
    nextToolbarId++;
  } // use a ref to these as changing the memoized mergedModules etc values causes unnecessary re-mounts


  var onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;
  var onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  var onChangeSelectionRef = useRef(onChangeSelection);
  onChangeSelectionRef.current = onChangeSelection; // handle inserting images from a file

  var insertImage = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(file) {
      var _onImageUploadRef$cur;

      var getImageDataUrl, getImage, image, index;
      return regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              getImageDataUrl = function getImageDataUrl(file) {
                return new Promise(function (resolve) {
                  var reader = new FileReader();

                  reader.onloadend = function () {
                    resolve(typeof reader.result === "string" ? reader.result : "");
                  };

                  reader.readAsDataURL(file);
                });
              };

              getImage = (_onImageUploadRef$cur = onImageUploadRef.current) !== null && _onImageUploadRef$cur !== void 0 ? _onImageUploadRef$cur : getImageDataUrl;
              _context.next = 4;
              return getImage(file);

            case 4:
              image = _context.sent;

              if (!(!image || !quill.current)) {
                _context.next = 7;
                break;
              }

              return _context.abrupt("return");

            case 7:
              index = (quill.current.getSelection() || {}).index;
              if (index === undefined || index < 0) index = quill.current.getLength();
              quill.current.insertEmbed(index, "uploaded-image", image, "user");

            case 10:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), []); // handle drag-and-drop or copy-and-paste of files

  var handleDataTransfer = useCallback(function (transfer) {
    var _transfer$files;

    if (!(transfer !== null && transfer !== void 0 && (_transfer$files = transfer.files) !== null && _transfer$files !== void 0 && _transfer$files.length)) return;

    for (var i = 0; i < transfer.files.length; i++) {
      var _file = transfer.files[i];

      if (disableImageTransfer || !(allowedImageTypes !== null && allowedImageTypes !== void 0 && allowedImageTypes.includes(_file.type))) {
        continue;
      }

      insertImage(_file);
      if (disableMultipleImages) break;
    }
  }, [insertImage, disableImageTransfer, disableMultipleImages]);
  var onEditorChange = useCallback(function (eventName, deltaOrRange, oldContentsOrRange, source) {
    if (!quill.current) return;

    if (eventName === "text-change" && onChangeRef.current) {
      var _quill$current;

      var newValue = (_quill$current = quill.current) === null || _quill$current === void 0 ? void 0 : _quill$current.getSemanticHTML();

      if (sanitizeEmptyHtml && newValue === EMPTY_HTML) {
        newValue = "";
      } // temporary workaround - getSemanticHTML is not preserving the
      // style attribute of the list container


      newValue = newValue.replaceAll("<ol><li>", "<ol style=\"margin:0; padding-inline-start: 2em; list-style-position:outside;\"><li>");
      newValue = newValue.replaceAll("<ul><li>", "<ul style=\"margin:0; padding-inline-start: 2em; list-style-position:outside;\"><li>"); // temporary workaround - preserve empty block blot line breaks

      newValue = newValue.replaceAll("<div></div>", "<div><br></div>");
      editorValue.current = newValue;
      onChangeRef.current(newValue, deltaOrRange, source, quill.current);
    } else if (eventName === "selection-change" && onChangeSelectionRef.current) {
      editorSelection.current = deltaOrRange;
      onChangeSelectionRef.current(deltaOrRange, source, quill.current);
    }
  }, [sanitizeEmptyHtml]);
  var mergedModules = useMemo(function () {
    var _initialModules$toolb, _initialModules$toolb2, _initialModules$toolb3;

    return _objectSpread2(_objectSpread2({}, initialModules), {}, {
      history: _objectSpread2({
        delay: 1000,
        maxStack: 100,
        userOnly: true
      }, initialModules === null || initialModules === void 0 ? void 0 : initialModules.history),
      toolbar: {
        container: (_initialModules$toolb = initialModules === null || initialModules === void 0 ? void 0 : (_initialModules$toolb2 = initialModules.toolbar) === null || _initialModules$toolb2 === void 0 ? void 0 : _initialModules$toolb2.container) !== null && _initialModules$toolb !== void 0 ? _initialModules$toolb : "#".concat(toolbarId.current),
        handlers: _objectSpread2({
          link: function link() {
            setInsertLinkOpen(true);
          },
          undo: function undo() {
            quill.current && quill.current.history.undo();
          },
          redo: function redo() {
            quill.current && quill.current.history.redo();
          },
          image: function image() {
            var fileInput = document.querySelector("input.ql-image[type=file]");

            if (!fileInput) {
              fileInput = document.createElement("input");
              fileInput.setAttribute("type", "file");
              fileInput.setAttribute("accept", (allowedImageTypes !== null && allowedImageTypes !== void 0 ? allowedImageTypes : []).join(", "));

              if (!disableMultipleImages) {
                fileInput.setAttribute("multiple", "");
              }

              fileInput.classList.add("ql-image");
              fileInput.addEventListener("change", function (e) {
                var _e$target$files;

                if (((_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files.length) > 0) {
                  for (var i = 0; i < e.target.files.length; i++) {
                    var _file2 = e.target.files[i];
                    if (!(allowedImageTypes !== null && allowedImageTypes !== void 0 && allowedImageTypes.includes(_file2.type))) continue;
                    insertImage(_file2);
                    if (disableMultipleImages) break;
                  }
                }
              });
            }

            fileInput.click();
          }
        }, initialModules === null || initialModules === void 0 ? void 0 : (_initialModules$toolb3 = initialModules.toolbar) === null || _initialModules$toolb3 === void 0 ? void 0 : _initialModules$toolb3.handlers)
      }
    });
  }, [initialModules, insertImage, disableMultipleImages]);
  var externalProxy = useMemo(function () {
    return {
      getEditor: function getEditor() {
        if (!quill.current) throw new Error("Accessing non-instantiated editor");
        return quill.current;
      },
      focus: function focus() {
        if (!quill.current) return;
        quill.current.focus();
      },
      blur: function blur() {
        if (!quill.current) return;
        editorSelection.current = null;
        quill.current.blur();
      }
    };
  }, []); // setup quill

  useEffect(function () {
    if (!container.current) return;
    var editorContainer = container.current.appendChild(container.current.ownerDocument.createElement("div"));
    quill.current = new Quill(editorContainer, {
      placeholder: placeholder,
      registry: registry,
      modules: mergedModules,
      theme: "snow"
    });
    var onChange = onEditorChange;
    quill.current.on("editor-change", onChange);
    quill.current.setContents(quill.current.clipboard.convert({
      html: editorValue.current
    }));

    if (pasteAsPlainText) {
      quill.current.clipboard.addMatcher(Node.ELEMENT_NODE, function (_, delta) {
        delta.ops = delta.ops.filter(function (op) {
          return typeof op.insert === "string";
        }).map(function (op) {
          return {
            insert: op.insert
          };
        });
        return delta;
      });
    }

    var handleDrop = function handleDrop(e) {
      return e.dataTransfer && handleDataTransfer(e.dataTransfer);
    };

    var handlePaste = function handlePaste(e) {
      return e.clipboardData && handleDataTransfer(e.clipboardData);
    };

    editorContainer.addEventListener("drop", handleDrop, false);
    editorContainer.addEventListener("paste", handlePaste, false);
    return function () {
      if (!quill.current) return;
      editorContainer.removeEventListener("drop", handleDrop, false);
      editorContainer.removeEventListener("paste", handlePaste, false);
      quill.current.off("editor-change", onChange);
      quill.current = null;
    };
  }, [onEditorChange, handleDataTransfer, mergedModules, pasteAsPlainText]); // apply value prop

  useEffect(function () {
    if (value === undefined || !quill.current) return;

    if (value !== editorValue.current) {
      var _quill$current2;

      editorValue.current = value;
      (_quill$current2 = quill.current) === null || _quill$current2 === void 0 ? void 0 : _quill$current2.setContents(quill.current.clipboard.convert({
        html: value
      }));
    }
  }, [value]); // apply read-only / disabled prop

  useEffect(function () {
    if (!quill.current) return;
    quill.current.enable(!readOnly && !disabled);
  }, [readOnly, disabled]); // expose a proxy to quill via the quillRef prop

  useEffect(function () {
    var assign = function assign(v) {
      if (typeof quillRef === "function") {
        quillRef(v);
      } else if (quillRef) {
        quillRef.current = v;
      }
    };

    assign(externalProxy);
    return function () {
      return assign(null);
    };
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(componentRoot.className, className, {
      readOnly: readOnly,
      disabled: disabled
    })
  }, ToolbarComponent && /*#__PURE__*/React.createElement(ToolbarComponent, _extends({}, toolbarProps, {
    id: toolbarId.current
  })), /*#__PURE__*/React.createElement("div", {
    className: "quill",
    ref: container
  }), /*#__PURE__*/React.createElement(QuillInsertLink, {
    quill: externalProxy,
    open: insertLinkOpen,
    onClose: function onClose() {
      return setInsertLinkOpen(false);
    }
  }));
}; // apply default quill theme


QuillEditorBase.propTypes = {
  value: propTypes.any,
  onChange: propTypes.func,
  modules: propTypes.any,
  registry: propTypes.any,
  toolbarComponent: propTypes.oneOfType([propTypes.elementType, propTypes.oneOf([null])]),
  toolbarProps: propTypes.any,
  onImageUpload: propTypes.func,
  allowedImageTypes: propTypes.oneOfType([propTypes.arrayOf(propTypes.string), propTypes.oneOf([null])]),
  disableImageTransfer: propTypes.bool,
  disableMultipleImages: propTypes.bool,
  sanitizeEmptyHtml: propTypes.bool,
  pasteAsPlainText: propTypes.bool,
  placeholder: propTypes.string,
  rows: propTypes.number,
  disabled: propTypes.bool,
  readOnly: propTypes.bool,
  error: propTypes.bool,
  quillRef: propTypes.any,
  className: propTypes.string,
  sx: propTypes.any
};
var StyledQuillEditor = styledQuillEditor(QuillEditorBase, componentRoot.styledOptions);
/**
 * An HTML editor based upon Quill
 */

var QuillEditor = function QuillEditor(_ref3) {
  var disabled = _ref3.disabled,
      error = _ref3.error,
      props = _objectWithoutProperties(_ref3, _excluded2);

  var rootProps = useThemeProps({
    props: props,
    name: componentRoot.name
  });
  var formControl = useFormControl();
  return /*#__PURE__*/React.createElement(StyledQuillEditor, _extends({}, rootProps, {
    disabled: disabled !== null && disabled !== void 0 ? disabled : formControl === null || formControl === void 0 ? void 0 : formControl.disabled,
    error: error !== null && error !== void 0 ? error : formControl === null || formControl === void 0 ? void 0 : formControl.error
  }));
};

var QuillEditor$1 = QuillEditor;

export { QuillEditor$1 as default };
